import { DownOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Dropdown,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import _ from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import VuiFormItemView from "../../../components/form-item-view";
import VuiNumberFormat from "../../../components/number-format";
import VuiPageTitle from "../../../components/page-title";
import { INBOX_STATUS_COLOR } from "../../../constants";
import { useBreadcrumb } from "../../../context/breadcrumb";
import { BreadcrumbActionType } from "../../../context/breadcrumb/reducer";
import {
  inboxStatusOptions,
  snakeCaseToTitleCase,
} from "../../../utils/helpers";
import { useFormInbox } from "../hooks/use-form-inbox";
import { useGetInbox } from "../hooks/use-get-inbox";

const { Text, Title } = Typography;

const InboxDetailModule = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { loadInboxData, isOnFetchingInboxData, inboxData } = useGetInbox();
  const { onRead, handleStatusUpdate, updateLoading } = useFormInbox();
  const title = `${t("common.text.inbox")} | `;
  const { dispatch } = useBreadcrumb();

  const availableStatusUpdate = useMemo(() => {
    const options = inboxStatusOptions(t);

    return options
      .filter((o) => inboxData?.available_statuses?.includes(o.id))
      .map((o) => ({
        label: o.name,
        key: o.id,
        disabled: updateLoading,
      }));
  }, [t, inboxData?.available_statuses, updateLoading]);

  const handleUpdate = useCallback(
    async (status: string) => {
      await handleStatusUpdate(id as string, status);
      await loadInboxData(id as string);
    },
    [handleStatusUpdate, id, loadInboxData]
  );

  useEffect(() => {
    dispatch({
      type: BreadcrumbActionType.Update,
      payload: [
        {
          link: "/",
          title: t("common.text.home"),
        },
        {
          link: "/inbox",
          title: t("common.text.inbox"),
        },
        {
          link: `/inbox/${id}`,
          title: inboxData?.fullname,
          isDisabled: true,
        },
      ],
    });
  }, [dispatch, t, inboxData?.fullname, id]);

  useEffect(() => {
    if (id) {
      (async () => {
        await loadInboxData(id, {
          with: ["meetings"],
        });
        await onRead(id);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <VuiPageTitle
            title={
              <Title level={4} style={{ fontWeight: 500, marginBottom: 0 }}>
                {title}
                <Text style={{ fontWeight: 700 }}>
                  {inboxData?.fullname || ""}
                </Text>
              </Title>
            }
            onBackLink="/inbox"
            rightTitleContent={[
              <Tag
                color={_.get(
                  INBOX_STATUS_COLOR,
                  inboxData?.status ?? "",
                  "blue"
                )}
              >
                {snakeCaseToTitleCase(inboxData?.status ?? " ")}
              </Tag>,
            ]}
          ></VuiPageTitle>
        </Col>
      </Row>

      <Spin size="large" spinning={isOnFetchingInboxData}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={16}>
            <Card
              title={t("common.text.information")}
              extra={
                <>
                  {inboxData?.can_update && (
                    <Dropdown
                      menu={{
                        items: availableStatusUpdate,
                        onClick: (e) => handleUpdate(e.key as string),
                      }}
                    >
                      <Button
                        style={{ marginTop: -10, marginBottom: -10 }}
                        type="default"
                        loading={updateLoading}
                      >
                        <Space>
                          {t("common.text.updateStatus")}
                          <DownOutlined />
                        </Space>
                      </Button>
                    </Dropdown>
                  )}
                </>
              }
              style={{ height: "100%" }}
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                  <VuiFormItemView
                    label={t("common.form.date.label")}
                    value={moment(inboxData?.created_at).format("D MMM YYYY")}
                  />
                </Col>
                <Col xs={24} md={8}>
                  <VuiFormItemView
                    label={t("common.form.category.label")}
                    value={inboxData?.category}
                  />
                </Col>
                <Col xs={24} md={8}>
                  <VuiFormItemView
                    label={t("common.form.name.label")}
                    value={inboxData?.fullname}
                  />
                </Col>
                <Col xs={24} md={8}>
                  <VuiFormItemView
                    label={t("common.form.email.label")}
                    value={inboxData?.email}
                  />
                </Col>
                <Col xs={24} md={8}>
                  <VuiFormItemView
                    label={t("common.form.number.label")}
                    value={inboxData?.number}
                  />
                </Col>
                <Col xs={24} md={8}>
                  <VuiFormItemView
                    label={t("common.form.company.label")}
                    value={inboxData?.company}
                  />
                </Col>
                <Col xs={24} md={8}>
                  <VuiFormItemView
                    label={t("common.form.website.label")}
                    value={inboxData?.website}
                  />
                </Col>
                <Col xs={24} md={8}>
                  <VuiFormItemView
                    label={t("common.form.budget.label")}
                    value={<VuiNumberFormat value={inboxData?.budget} />}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <VuiFormItemView
                    label={t("common.form.projectDescription.label")}
                    value={inboxData?.project_description}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card
              title={t("common.text.meetingInformation")}
              style={{ height: "100%" }}
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} md={24}>
                  <VuiFormItemView
                    label={t("common.form.meetingDate.label")}
                    value={moment(inboxData?.meetings?.start_time).format(
                      "D MMM YYYY"
                    )}
                  />
                </Col>
                <Col xs={24} md={24}>
                  <VuiFormItemView
                    label={t("common.form.meetingAttendance.label")}
                    value={
                      inboxData?.meetings?.attendees?.length
                        ? inboxData?.meetings?.attendees.join(", ")
                        : "-"
                    }
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Spin>
    </>
  );
};

export default InboxDetailModule;
